import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { APPLICATION_STATUS, LEAD_BASED_PAINT, STUDENT_CODE_TYPES } from '@shared/utils/app-static-data';
import { ConfirmationDialogueComponent } from 'app/components/common/confirmation-dialogue/confirmation-dialogue.component';
import { DocumentUploadDialogComponent, IDocumentUploadDialogResult } from 'app/components/common/document-upload-dialog/document-upload-dialog.component';
import { IDocumentFile } from 'app/models/document/document-file';
import { IIndividualAddress } from 'app/models/individuals/individual-address';
import { IIndividualApplicationDeficiency } from 'app/models/individuals/individual-application-deficiency';
import { IDocument } from 'app/models/document/document';
import { DOCUMENT_CATEGORIES } from 'app/models/document/document-categories';
import { DocumentService } from 'app/services/document/document.service';
import { IIndividualApplication } from 'app/models/individuals/individual-application';
import { IIndividualCertificate } from 'app/models/individuals/individual-certificate';
import { IIndividualContactInformation } from 'app/models/individuals/individual-contact-information';
import { IIndividual } from 'app/models/individuals/individual';
import { IIndividualWorkspace } from 'app/models/individuals/individual-workspace';
import { CommonService } from 'app/services/common/common.service';
import { FeatureFlagsService } from 'app/services/core/feature-flags.service';
import { SharedService } from 'app/services/core/shared.service';
import { IndividualService } from 'app/services/individuals/individual.service';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

interface IIndividualApplicationDialogData {
  applicationId?: number | null;
  personId?: number | null;
  workflow?: 'new' | 'link' | 'renewal';
  previousApplication?: string | null;
}

interface IRenewalOption {
  label: string;
  certificate: IIndividualCertificate;
}

type IIndividualApplicationWorkflow = 'chooser' | 'new' | 'link' | 'renewal';
type ILinkLookupMatchStatus = 'unknown' | 'none' | 'single' | 'multiple';

interface IWizardValidationError {
  field: string;
  label: string;
  step: string;
}

interface IValidationFeedbackGroup {
  step: string;
  errors: IWizardValidationError[];
}

@Component({
  selector: 'app-individual-application',
  templateUrl: './individual-application.component.html',
  styleUrls: ['./individual-application.component.scss']
})
export class IndividualApplicationComponent implements OnInit {
  public readonly reviewPlaceholder = 'Not Provided';
  public readonly approvalEmail = 'leadprogram@odhsoha.oregon.gov';
  public loading = true;
  public saving = false;
  public didPersistChange = false;
  public currentStep = 0;
  public selectedWorkflow: IIndividualApplicationWorkflow = 'new';
  public uiData = LEAD_BASED_PAINT;
  public application!: IIndividualApplication;
  public applicationForm: UntypedFormGroup;
  public attestationForm: UntypedFormGroup;
  public documents: IDocument[] = [];
  public filteredDocuments: IDocument[] = [];
  public documentsFilterString = '';
  public linkLookupLoading = false;
  public linkApprovalContactEmails: string[] = [];
  public linkLookupMatchStatus: ILinkLookupMatchStatus = 'unknown';
  public matchedLinkApprovalEmail: string | null = null;
  public renewalOptions: IRenewalOption[] = [];
  public validationSummaryRequested = false;
  public isFeedbackCollapsed = false;
  public filteredDeficiencyStatus = new FormControl('both');
  public isMobile = false;

  @ViewChild('validationSummaryCard') validationSummaryCard?: ElementRef<HTMLElement>;

  private readonly standardStepLabels = ['Application Information', 'Individual Information', 'Contact Information', 'Mailing Address', 'Documents', 'Review & Submit'];
  private readonly linkStepLabels = ['Individual Information', 'Signature and Acknowledgments'];
  private readonly submittedLinkStepLabels = ['Individual Information', 'Signature and Acknowledgments', 'Confirmation'];
  private readonly phonePattern = /^\(?([0-9]{3})\)?[-\s]*([0-9]{3})[-\s]*([0-9]{4}).*$/;
  private readonly zipPattern = /^\d{5}(-\d{4})?$/;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IIndividualApplicationDialogData,
    private readonly dialogRef: MatDialogRef<IndividualApplicationComponent>,
    private readonly dialog: MatDialog,
    private readonly featureFlags: FeatureFlagsService,
    private readonly breakpointObserver: BreakpointObserver,
    public readonly sharedService: SharedService,
    private readonly commonService: CommonService,
    private readonly documentService: DocumentService,
    public readonly individualService: IndividualService,
    private readonly toastr: ToastrService,
  ) {
    this.applicationForm = this.createApplicationForm();
    this.attestationForm = new UntypedFormGroup({
      attestation: new UntypedFormControl(false, Validators.requiredTrue),
      esignature: new UntypedFormControl('', Validators.required),
    });
  }

  ngOnInit(): void {
    if (!this.featureFlags.enableIndividuals) {
      this.dialogRef.close(false);
      return;
    }

    this.breakpointObserver.observe([
      Breakpoints.Handset,
      Breakpoints.Tablet,
      Breakpoints.Small,
    ]).subscribe((result) => {
      this.isMobile = result.matches;
    });

    this.selectedWorkflow = this.resolveInitialWorkflow();

    if (this.data.applicationId) {
      this.loadExistingApplication(this.data.applicationId);
      return;
    }

    this.initializeNewApplication();
  }

  get dialogTitle(): string {
    const baseTitle = this.selectedWorkflow === 'renewal'
      ? 'Individual Renewal Application'
      : this.isLinkWorkflow
        ? 'Individual Link Request'
        : 'Individual Application';
    const applicationStatus = this.displayApplicationStatus;

    if (this.showWorkflowChooser || !applicationStatus) {
      return baseTitle;
    }

    return baseTitle + ' - ' + applicationStatus;
  }

  get displayApplicationStatus(): string {
    if (!this.application?.status) {
      return '';
    }

    return this.isApplicationExpired(this.application) ? 'Expired' : this.application.status;
  }

  get showApplicationStatusSummary(): boolean {
    return !!this.application?.id && !!this.displayApplicationStatus;
  }

  get applicationStatusDateLabel(): string {
    const status = this.normalizeStatus(this.application?.status);

    if (status === this.normalizeStatus(APPLICATION_STATUS.approved)) {
      return 'Approved On';
    }

    if (status === this.normalizeStatus(APPLICATION_STATUS.cancelled)) {
      return 'Cancelled On';
    }

    return '';
  }

  get applicationStatusDate(): string | null {
    const status = this.normalizeStatus(this.application?.status);

    if (status === this.normalizeStatus(APPLICATION_STATUS.approved)) {
      return this.application?.approvalDate || null;
    }

    if (status === this.normalizeStatus(APPLICATION_STATUS.cancelled)) {
      return this.application?.lastUpdated || null;
    }

    return null;
  }

  get isLinkWorkflow(): boolean {
    return this.selectedWorkflow === 'link';
  }

  get showWorkflowChooser(): boolean {
    return this.selectedWorkflow === 'chooser';
  }

  get canReturnToChooser(): boolean {
    return !this.showWorkflowChooser
      && !this.data.applicationId
      && !this.data.personId
      && this.selectedWorkflow !== 'renewal'
      && (this.application?.id || 0) === 0;
  }

  get canEdit(): boolean {
    if (!this.application) {
      return false;
    }

    return this.isDraftStatus(this.application.status) && !this.isApplicationExpired(this.application);
  }

  get isRenewalApplication(): boolean {
    return this.selectedWorkflow === 'renewal'
      || this.application?.renewal === true
      || this.applicationForm.get('renewal')?.value === true;
  }

  get showMailingCounty(): boolean {
    return this.applicationForm.get('mailingState')?.value === 'OR';
  }

  get isSubmitted(): boolean {
    return this.application?.status === APPLICATION_STATUS.submitted;
  }

  /** Documents are locked once the application has been submitted for review. */
  get canDeleteDocuments(): boolean {
    return this.canEdit
      && this.normalizeStatus(this.application?.status) !== this.normalizeStatus(APPLICATION_STATUS.submitted);
  }

  get hasLinkApprovalContactEmails(): boolean {
    return this.linkApprovalContactEmails.length > 0;
  }

  get hasMultipleLinkApprovalContactEmails(): boolean {
    return this.linkApprovalContactEmails.length > 1;
  }

  get hasUniqueLinkMatch(): boolean {
    return this.linkLookupMatchStatus === 'single';
  }

  get hasMultipleLinkMatches(): boolean {
    return this.linkLookupMatchStatus === 'multiple';
  }

  get hasNoLinkMatch(): boolean {
    return this.linkLookupMatchStatus === 'none';
  }

  get showMatchedLinkApprovalEmail(): boolean {
    return this.hasUniqueLinkMatch && this.hasText(this.matchedLinkApprovalEmail);
  }

  close(): void {
    if (!this.canEdit) {
      this.dialogRef.close(this.didPersistChange);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '420px',
      data: {
        title: 'Exit without saving?',
        message: 'Any unsaved data will be lost.\n\nAre you sure you want to exit?',
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.dialogRef.close(this.didPersistChange);
      }
    });
  }

  onLinkLookupInputChange(): void {
    if (this.application?.person || this.linkApprovalContactEmails.length > 0) {
      this.resetLinkSelectionState();
    }
  }

  onStepSelectionChange(event: StepperSelectionEvent): void {
    if (this.loading || this.saving) {
      return;
    }

    this.navigateToStep(event.selectedIndex);
  }

  loadLinkIndividualDetails(showValidationFeedback = false, continuation?: () => void): void {
    if (!this.isLinkWorkflow || this.linkLookupLoading) {
      return;
    }

    const firstNameControl = this.applicationForm.get('firstName');
    const lastNameControl = this.applicationForm.get('lastName');
    const firstName = (firstNameControl?.value || '').trim();
    const lastName = (lastNameControl?.value || '').trim();
    const normalizedFirstName = firstName.toLowerCase();
    const normalizedLastName = lastName.toLowerCase();
    const dateOfBirth = this.normalizeDateString(this.applicationForm.get('dateOfBirth')?.value);
    const phone = (this.applicationForm.get('linkLookupPhone')?.value || '').trim();
    const email = (this.applicationForm.get('linkLookupEmail')?.value || '').trim();

    if (!firstName || !lastName || !this.hasLinkLookupSupplementalValue()) {
      this.resetLinkSelectionState();
      return;
    }

    if (this.application.person && this.selectedLinkIndividualMatches()) {
      continuation?.();
      return;
    }

    this.linkLookupLoading = true;
    this.individualService.searchIndividuals({
      firstName,
      lastName,
      dateOfBirth: this.applicationForm.get('dateOfBirth')?.value,
      phone,
      email,
    }).subscribe({
      next: (results) => {
        if (!this.linkLookupRequestMatchesCurrentForm(normalizedFirstName, normalizedLastName, dateOfBirth, phone, email)) {
          this.finalizeLinkLookup();
          return;
        }

        const matchedIndividuals = (results || []).filter((individual) => this.matchesLinkSearch(individual));
        if (matchedIndividuals.length === 1) {
          this.application.person = matchedIndividuals[0];
          this.linkLookupMatchStatus = 'single';
          this.matchedLinkApprovalEmail = this.resolveMatchedLinkApprovalEmail([]);
          this.loadLinkApprovalContactEmails(this.application.person.id);
        } else {
          this.application.person = null;
          this.linkApprovalContactEmails = [];
          this.matchedLinkApprovalEmail = null;
          this.linkLookupMatchStatus = matchedIndividuals.length > 1 ? 'multiple' : 'none';
        }

        this.finalizeLinkLookup();
        continuation?.();
      },
      error: () => {
        if (!this.linkLookupRequestMatchesCurrentForm(normalizedFirstName, normalizedLastName, dateOfBirth, phone, email)) {
          this.finalizeLinkLookup();
          return;
        }

        this.resetLinkSelectionState();
        this.finalizeLinkLookup();
        continuation?.();
      }
    });
  }

  selectNewApplication(): void {
    this.beginWorkflow('new');
  }

  selectLinkApplication(): void {
    this.beginWorkflow('link');
  }

  showPreviousButton(): boolean {
    if (this.showWorkflowChooser || this.isSubmittedLinkConfirmationStep()) {
      return false;
    }

    return this.currentStep > 0 || this.canReturnToChooser;
  }

  showCancelButton(): boolean {
    if (this.showWorkflowChooser) {
      return false;
    }

    if (this.isLinkWorkflow) {
      return true;
    }

    return !!this.application?.id && this.canEdit;
  }

  isCancelButtonDisabled(): boolean {
    if (!this.application?.id) {
      return true;
    }

    const status = this.application.status;
    return this.saving
      || status === APPLICATION_STATUS.cancelled
      || status === APPLICATION_STATUS.approved
      || this.isApplicationExpired(this.application);
  }

  getCancelButtonText(): string {
    if (this.isLinkWorkflow) {
      return 'Cancel Link Request';
    }

    return this.isSubmitted ? 'Withdraw' : 'Cancel';
  }

  showNextButton(): boolean {
    return !this.showWorkflowChooser && this.currentStep < this.getLastStepIndex();
  }

  showSubmitButton(): boolean {
    if (this.showWorkflowChooser || this.currentStep !== this.getLastStepIndex() || !this.canEdit) {
      return false;
    }

    return true;
  }

  isSubmitButtonDisabled(): boolean {
    if (!this.canEdit || this.saving) {
      return true;
    }

    return this.isLinkWorkflow ? this.linkLookupLoading : false;
  }

  goToNextStep(): void {
    if (this.saving || this.loading) {
      return;
    }

    this.navigateToStep(this.currentStep + 1);
  }

  goToPreviousStep(): void {
    if (this.currentStep === 0 && this.canReturnToChooser) {
      this.selectedWorkflow = 'chooser';
      this.resetWorkflowState();
      return;
    }

    if (this.currentStep > 0) {
      this.navigateToStep(this.currentStep - 1);
    }
  }

  hasValidationErrors(): boolean {
    return this.validationSummaryRequested && this.getValidationErrors().length > 0;
  }

  hasFeedbackContent(): boolean {
    return this.individualService.shouldDisplayApplicationDeficiencies(this.application) || this.hasValidationErrors();
  }

  toggleFeedbackPanel(): void {
    this.isFeedbackCollapsed = !this.isFeedbackCollapsed;
  }

  stepHasValidationErrors(stepIndex: number): boolean {
    return this.validationSummaryRequested && this.getStepValidationErrors(stepIndex).length > 0;
  }

  getValidationFeedbackGroups(): IValidationFeedbackGroup[] {
    if (!this.validationSummaryRequested) {
      return [];
    }

    const validationErrors = this.getValidationErrors();
    if (validationErrors.length === 0) {
      return [];
    }

    const groupedErrors = new Map<string, IWizardValidationError[]>();
    validationErrors.forEach((error) => {
      const existing = groupedErrors.get(error.step) || [];
      if (!existing.some((candidate) => candidate.label === error.label)) {
        existing.push(error);
      }
      groupedErrors.set(error.step, existing);
    });

    const orderedGroups: IValidationFeedbackGroup[] = this.getStepLabels()
      .filter((stepLabel) => groupedErrors.has(stepLabel))
      .map((stepLabel) => ({ step: stepLabel, errors: groupedErrors.get(stepLabel) || [] }));

    groupedErrors.forEach((errors, step) => {
      if (!orderedGroups.some((group) => group.step === step)) {
        orderedGroups.push({ step, errors });
      }
    });

    return orderedGroups.filter((group) => group.errors.length > 0);
  }

  focusValidationFeedbackError(error: IWizardValidationError): void {
    const stepIndex = this.getStepLabels().findIndex((stepLabel) => stepLabel === error.step);
    if (stepIndex >= 0) {
      this.navigateToStep(stepIndex);
    }

    const selectors = this.resolveValidationFieldSelectors(error);
    setTimeout(() => {
      if (this.focusFirstVisibleBySelector(selectors)) {
        return;
      }

      if (this.focusFirstVisibleBySelector(this.getDefaultInvalidFieldSelectors())) {
        return;
      }

      this.getVisibleScrollContainer()?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
  }

  getCombinedFeedback(): string {
    const sections: string[] = [];

    const deficiencyDescriptions = this.getPersistedDeficiencyDescriptions();
    if (deficiencyDescriptions.length > 0) {
      sections.push(deficiencyDescriptions.join('\n\n'));
    }

    const validationFeedback = this.getValidationFeedbackText();
    if (validationFeedback) {
      sections.push(validationFeedback);
    }

    return sections.join('\n\n').trim();
  }

  getPersistedDeficiencyDescriptions(): string[] {
    return this.individualService.getUnresolvedApplicationDeficiencyDescriptions(this.application);
  }

  showApplicationControlError(controlName: string, errorName?: string): boolean {
    const control = this.applicationForm.get(controlName);
    if (!control) {
      return false;
    }

    if (errorName) {
      if (errorName === 'required') {
        return !this.hasText(control.value) && this.hasValidationInteraction(control);
      }

      return control.hasError(errorName) && this.hasValidationInteraction(control);
    }

    return control.invalid && this.hasValidationInteraction(control);
  }

  showAttestationError(controlName: string, errorName?: string): boolean {
    const control = this.attestationForm.get(controlName);
    if (!control) {
      return false;
    }

    if (errorName) {
      if (errorName === 'requiredTrue') {
        return (control.hasError('requiredTrue') || control.hasError('required')) && this.hasValidationInteraction(control);
      }

      return control.hasError(errorName) && this.hasValidationInteraction(control);
    }

    return control.invalid && this.hasValidationInteraction(control);
  }

  showMailingRequiredError(controlName: string): boolean {
    const control = this.applicationForm.get(controlName);
    return !this.hasText(control?.value) && this.hasValidationInteraction(control);
  }

  showRenewalSelectionError(): boolean {
    const control = this.applicationForm.get('previousApplication');
    return this.isRenewalApplication && !this.hasText(control?.value) && this.hasValidationInteraction(control);
  }

  saveDraft(showToast = true, continuation?: () => void): void {
    if (!this.canEdit || this.isLinkWorkflow) {
      return;
    }

    this.syncApplicationFromForms();
    this.persistApplication(this.application.status || APPLICATION_STATUS.inProgress, showToast ? 'Draft saved.' : null, continuation);
  }

  submitApplication(): void {
    if (this.isLinkWorkflow) {
      this.submitLinkApplication();
      return;
    }

    this.validationSummaryRequested = true;
    this.applicationForm.markAllAsTouched();
    this.applicationForm.updateValueAndValidity({ emitEvent: false });
    this.attestationForm.markAllAsTouched();

    const validationErrors = this.getValidationErrors();
    if (validationErrors.length > 0) {
      this.handleValidationFailure(validationErrors, 'Complete the required fields before submitting.');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '420px',
      data: {
        title: 'Individual Application Submission Confirmation',
        message: '<strong>You are about to submit your Individual Application.</strong>\n\nAre you sure you want to submit this application?',
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.syncApplicationFromForms();
      this.persistApplication(APPLICATION_STATUS.submitted, 'Individual application submitted.', () => this.dialogRef.close(true));
    });
  }

  submitLinkApplication(): void {
    this.validationSummaryRequested = true;
    this.applicationForm.get('firstName')?.markAsTouched();
    this.applicationForm.get('lastName')?.markAsTouched();
    this.applicationForm.get('dateOfBirth')?.markAsTouched();
    this.applicationForm.get('linkLookupPhone')?.markAsTouched();
    this.applicationForm.get('linkLookupEmail')?.markAsTouched();
    this.attestationForm.markAllAsTouched();

    const validationErrors = this.getValidationErrors();
    if (validationErrors.length > 0) {
      this.handleValidationFailure(validationErrors, 'Complete the required fields before submitting the link request.');
      return;
    }

    const showConfirmationDialog = () => {
      const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
        width: '420px',
        data: {
          title: 'Individual Link Request Confirmation',
          message: `<strong>You are about to submit an Individual account-link request.</strong>\n\n${this.getLinkApprovalRecipientSummary()}\n\nThis request will be reviewed and processed before access can be granted, and you will be notified by email once your request has been reviewed.\n\nAre you sure you want to submit this request?`,
        }
      });

      dialogRef.afterClosed().subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }

        const completeSubmission = () => {
          this.syncApplicationFromForms();
          this.persistApplication(APPLICATION_STATUS.submitted, 'Individual link request submitted. You will be notified after review.', () => {
            this.applicationForm.disable({ emitEvent: false });
            this.attestationForm.disable({ emitEvent: false });
            this.currentStep = this.getLastStepIndex();
          });
        };

        if (this.canAutoSearchLinkLookup()) {
          this.loadLinkIndividualDetails(false, completeSubmission);
          return;
        }

        completeSubmission();
      });
    };

    const openConfirmation = () => {
      const linkedPersonId = this.application.person?.id || 0;
      if (linkedPersonId > 0) {
        this.guardDuplicateIndividualLinkSubmission(linkedPersonId, showConfirmationDialog);
        return;
      }

      showConfirmationDialog();
    };

    if (this.canAutoSearchLinkLookup()) {
      this.loadLinkIndividualDetails(false, openConfirmation);
      return;
    }

    openConfirmation();
  }

  cancelApplication(): void {
    if (!this.application) {
      return;
    }

    const isLinkRequest = this.isLinkWorkflow;
    const message = isLinkRequest
      ? 'Are you sure you want to cancel this link request? This action will mark the link request as cancelled.'
      : this.isSubmitted
        ? '<strong>You are about to withdraw your Individual Application.</strong>\nYour application will return to <strong>In Progress</strong> so you can edit it again.\n\nAre you sure?'
        : 'You are about to cancel this Individual Application.\n\nAre you sure you want to continue?';
    const title = isLinkRequest
      ? 'Cancel Link Request?'
      : this.isSubmitted
        ? 'Withdraw Individual Application Confirmation'
        : 'Cancel Individual Application Confirmation';

    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '420px',
      data: { title, message }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.syncApplicationFromForms();
      const nextStatus = isLinkRequest
        ? APPLICATION_STATUS.cancelled
        : this.isSubmitted
          ? APPLICATION_STATUS.inProgress
          : APPLICATION_STATUS.cancelled;
      const successMessage = isLinkRequest
        ? 'Link request cancelled.'
        : this.isSubmitted
          ? 'Application withdrawn.'
          : 'Application cancelled.';

      this.persistApplication(nextStatus, successMessage, () => this.dialogRef.close(true));
    });
  }


  openDocumentUploadDialog(): void {
    if (!this.canEdit) {
      return;
    }

    const dialogRef = this.dialog.open(DocumentUploadDialogComponent, {
      width: this.sharedService.isMobile ? '95%' : '560px',
      autoFocus: false,
      data: {
        title: 'Upload Individual Application Document',
        allowAssociation: false,
      },
    });

    dialogRef.afterClosed().subscribe((result: IDocumentUploadDialogResult | undefined) => {
      if (!result?.file) {
        return;
      }

      this.ensureApplicationSaved(() => {
        this.saveDocumentFile(result.file, result.description);
      });
    });
  }

  filterDocumentTable(): void {
    const filterText = this.documentsFilterString.trim().toLowerCase();
    this.filteredDocuments = this.documents.filter((document) => {
      if (!filterText) {
        return true;
      }

      return [document.fileName || '', document.fileDescription || ''].join(' ').toLowerCase().includes(filterText);
    });
  }

  downloadDocument(document: IDocument): void {
    if (!document?.id) {
      return;
    }

    this.documentService.downloadDocument(document).subscribe({
      error: (error) => this.toastr.error(error?.error ?? 'Unable to download the document.'),
    });
  }

  deleteDocument(document: IDocument): void {
    if (!this.canDeleteDocuments || !document?.id) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '420px',
      data: {
        title: 'Delete Application Document',
        message: 'Are you sure you want to remove <strong>' + document.fileName + '</strong> from this application?',
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.documentService.deleteDocument(document.id).subscribe({
        next: () => {
          this.documents = this.documents.filter((currentDocument) => currentDocument.id !== document.id);
          this.filterDocumentTable();
          this.didPersistChange = true;
        },
        error: (error) => this.toastr.error(error?.error ?? 'Unable to delete the document.'),
      });
    });
  }

  onStateSelectionChange(): void {
    if (this.applicationForm.get('mailingState')?.value !== 'OR') {
      this.applicationForm.get('mailingCounty')?.setValue('');
    }

    this.applicationForm.get('mailingCounty')?.updateValueAndValidity({ emitEvent: false });
  }

  getCertificateTypeValues(): string[] {
    return Object.values(STUDENT_CODE_TYPES).sort();
  }

  getCertificateTypeLabel(certificateType: string | null | undefined): string {
    return certificateType || this.reviewPlaceholder;
  }

  getApplicantName(): string {
    return [this.application?.firstName, this.application?.lastName].filter(Boolean).join(' ') || this.reviewPlaceholder;
  }

  getReviewValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return this.reviewPlaceholder;
    }

    if (value instanceof Date) {
      return this.formatUsDate(value, this.reviewPlaceholder);
    }

    return value.toString();
  }

  getFilteredDeficiencies(): IIndividualApplicationDeficiency[] {
    if (this.filteredDeficiencyStatus.value === 'true') {
      return (this.application?.deficiencies || []).filter((deficiency) => deficiency.isResolved);
    }

    if (this.filteredDeficiencyStatus.value === 'false') {
      return (this.application?.deficiencies || []).filter((deficiency) => !deficiency.isResolved);
    }

    return this.application?.deficiencies || [];
  }

  statusColor(status: string): string {
    return this.sharedService.getStatusColor(status);
  }

  applyRenewalSelection(controlNumber: string): void {
    const option = this.renewalOptions.find((renewalOption) => renewalOption.certificate.controlNumber === controlNumber);
    if (!option) {
      return;
    }

    const viableDate = option.certificate.expirationDate
      ? new Date(new Date(option.certificate.expirationDate).setDate(new Date(option.certificate.expirationDate).getDate() + 1)).toISOString()
      : null;

    this.application.certificateType = option.certificate.codeType;
    this.application.previousApplication = option.certificate.controlNumber || null;
    this.application.applicationViableDate = viableDate;

    this.patchFormsFromApplication();
  }

  private initializeNewApplication(): void {
    this.resetWorkflowState();

    if (this.showWorkflowChooser) {
      this.loading = false;
      return;
    }

    if (this.selectedWorkflow === 'renewal' && this.data.personId) {
      this.loadRenewalContext(this.data.personId);
      return;
    }

    if (this.data.personId && !this.isLinkWorkflow) {
      this.prefillFromWorkspace(this.data.personId);
      return;
    }

    this.loading = false;
  }

  private loadExistingApplication(applicationId: number): void {
    forkJoin({
      application: this.individualService.getIndividualApplicationById(applicationId),
      documents: this.documentService.getDocuments({ ownerType: 'PersonApplication', ownerId: applicationId }).pipe(catchError(() => of([]))),
      deficiencies: this.individualService.getIndividualApplicationDeficiencies(applicationId).pipe(catchError(() => of([]))),
    }).subscribe({
      next: (result) => {
        this.application = result.application;
        this.application.deficiencies = result.deficiencies;
        this.selectedWorkflow = this.getWorkflowFromApplication(result.application);
        this.documentsFilterString = '';
        this.documents = result.documents;
        this.filterDocumentTable();
        this.patchFormsFromApplication();

        if (this.isLinkWorkflow && this.application.person?.id) {
          this.loadSavedLinkSummary();
        }

        if (!this.canEdit) {
          this.applicationForm.disable({ emitEvent: false });
          this.attestationForm.disable({ emitEvent: false });
        }

        this.currentStep = this.getInitialStepIndex();
        this.linkLookupLoading = false;
        this.validationSummaryRequested = false;
        this.loading = false;
      },
      error: () => {
        this.toastr.error('Unable to load the Individual application.');
        this.dialogRef.close(false);
      }
    });
  }

  private loadRenewalContext(personId: number): void {
    this.individualService.getIndividualWorkspace(personId).subscribe({
      next: (workspace) => {
        this.applyWorkspaceToApplication(workspace);
        this.application.renewal = true;
        this.renewalOptions = this.buildRenewalOptions(workspace.certificates || []);

        const requestedCertificate = this.data.previousApplication;
        if (requestedCertificate) {
          this.applyRenewalSelection(requestedCertificate);
        } else if (this.renewalOptions.length) {
          this.applyRenewalSelection(this.renewalOptions[0].certificate.controlNumber || '');
        }

        this.patchFormsFromApplication();
        this.currentStep = 0;
        this.linkLookupLoading = false;
        this.validationSummaryRequested = false;
        this.loading = false;
      },
      error: () => {
        this.toastr.error('Unable to load the Individual renewal context.');
        this.dialogRef.close(false);
      }
    });
  }

  private prefillFromWorkspace(personId: number): void {
    this.individualService.getIndividualWorkspace(personId).subscribe({
      next: (workspace) => {
        this.applyWorkspaceToApplication(workspace);
        this.patchFormsFromApplication();
        this.loading = false;
      },
      error: () => {
        this.patchFormsFromApplication();
        this.loading = false;
      }
    });
  }

  private loadSavedLinkSummary(): void {
    if (!this.application.person?.id) {
      return;
    }

    this.linkLookupMatchStatus = 'single';
    this.loadLinkApprovalContactEmails(this.application.person.id);
  }

  private createApplicationForm(): UntypedFormGroup {
    return new UntypedFormGroup({
      certificateType: new UntypedFormControl('', Validators.required),
      renewal: new UntypedFormControl(false),
      previousApplication: new UntypedFormControl(''),
      isPublic: new UntypedFormControl(false),
      firstName: new UntypedFormControl('', Validators.required),
      middleName: new UntypedFormControl(''),
      lastName: new UntypedFormControl('', Validators.required),
      title: new UntypedFormControl(''),
      ssn: new UntypedFormControl('', [Validators.pattern(/^(?:\*{7}|^\(?([0-9]{3})\)?-?([0-9]{2})-?([0-9]{4})).*$/)]),
      ssnExempt: new UntypedFormControl(true),
      ssnExemptStatement: new UntypedFormControl(''),
      dateOfBirth: new UntypedFormControl(''),
      linkLookupPhone: new UntypedFormControl('', Validators.pattern(this.phonePattern)),
      linkLookupEmail: new UntypedFormControl('', Validators.email),
      phone: new UntypedFormControl('', [Validators.required, Validators.pattern(this.phonePattern)]),
      alternatePhone: new UntypedFormControl('', Validators.pattern(this.phonePattern)),
      faxPhone: new UntypedFormControl('', Validators.pattern(this.phonePattern)),
      email: new UntypedFormControl('', [Validators.required, Validators.email]),
      confirmEmail: new UntypedFormControl('', [Validators.required, Validators.email]),
      website: new UntypedFormControl(''),
      repLabel: new UntypedFormControl(''),
      repPhone: new UntypedFormControl('', Validators.pattern(this.phonePattern)),
      repAlternatePhone: new UntypedFormControl('', Validators.pattern(this.phonePattern)),
      repFaxPhone: new UntypedFormControl('', Validators.pattern(this.phonePattern)),
      repEmail: new UntypedFormControl('', Validators.email),
      repConfirmEmail: new UntypedFormControl('', Validators.email),
      repWebsite: new UntypedFormControl(''),
      mailingStreet: new UntypedFormControl('', Validators.required),
      mailingCity: new UntypedFormControl('', Validators.required),
      mailingState: new UntypedFormControl('OR', Validators.required),
      mailingZip: new UntypedFormControl('', [Validators.required, Validators.pattern(this.zipPattern)]),
      mailingCounty: new UntypedFormControl(''),
    }, {
      validators: [
        this.commonService.emailValidator('email', 'confirmEmail'),
        this.commonService.emailValidator('repEmail', 'repConfirmEmail'),
      ]
    });
  }

  private createEmptyApplication(): IIndividualApplication {
    return {
      id: 0,
      userID: null,
      person: null,
      status: APPLICATION_STATUS.inProgress,
      applicationDate: new Date().toISOString(),
      licenseDate: new Date().toISOString(),
      approverEmail: null,
      approvalDate: null,
      applicationViableDate: null,
      lastUpdatedBy: null,
      lastUpdated: null,
      isPublic: false,
      renewal: this.selectedWorkflow === 'renewal',
      previousApplication: null,
      ssn: '',
      dateOfBirth: null,
      title: '',
      firstName: '',
      middleName: '',
      lastName: '',
      phone: '',
      alternatePhone: '',
      faxPhone: '',
      email: '',
      confirmEmail: '',
      website: '',
      mailingStreet: '',
      mailingState: 'OR',
      mailingCity: '',
      mailingZip: '',
      mailingCounty: '',
      mailingIsPhysical: false,
      certificateType: this.selectedWorkflow === 'link' ? null : '',
      attestation: false,
      ssnExempt: true,
      ssnExemptStatement: '',
      esignature: '',
      repLabel: '',
      repPhone: '',
      repAlternatePhone: '',
      repFaxPhone: '',
      repEmail: '',
      repConfirmEmail: '',
      repWebsite: '',
      deficiencies: [],
    };
  }

  private patchFormsFromApplication(): void {
    this.applicationForm.patchValue({
      certificateType: this.application.certificateType || '',
      renewal: this.application.renewal === true,
      previousApplication: this.application.previousApplication || '',
      isPublic: this.application.isPublic === true,
      firstName: this.application.firstName || '',
      middleName: this.application.middleName || '',
      lastName: this.application.lastName || '',
      title: this.application.title || '',
      ssn: this.application.ssn || '',
      ssnExempt: true,
      ssnExemptStatement: this.application.ssnExemptStatement || '',
      dateOfBirth: this.toDateValue(this.application.dateOfBirth),
      linkLookupPhone: this.application.phone || '',
      linkLookupEmail: this.application.email || '',
      phone: this.application.phone || '',
      alternatePhone: this.application.alternatePhone || '',
      faxPhone: this.application.faxPhone || '',
      email: this.application.email || '',
      confirmEmail: this.application.confirmEmail || '',
      website: this.application.website || '',
      repLabel: this.application.repLabel || '',
      repPhone: this.application.repPhone || '',
      repAlternatePhone: this.application.repAlternatePhone || '',
      repFaxPhone: this.application.repFaxPhone || '',
      repEmail: this.application.repEmail || '',
      repConfirmEmail: this.application.repConfirmEmail || '',
      repWebsite: this.application.repWebsite || '',
      mailingStreet: this.application.mailingStreet || '',
      mailingCity: this.application.mailingCity || '',
      mailingState: this.application.mailingState || 'OR',
      mailingZip: this.application.mailingZip || '',
      mailingCounty: this.application.mailingState === 'OR' ? (this.application.mailingCounty || '') : '',
    });

    this.attestationForm.patchValue({
      attestation: this.application.attestation === true,
      esignature: this.application.esignature || '',
    });

    this.updateSsnRequirement();
  }

  private syncApplicationFromForms(): void {
    const rawValue = this.applicationForm.getRawValue();
    const attestationValue = this.attestationForm.getRawValue();

    this.application = {
      ...this.application,
      status: this.application.status || APPLICATION_STATUS.inProgress,
      applicationDate: this.application.applicationDate || new Date().toISOString(),
      licenseDate: this.application.licenseDate || this.application.applicationDate || new Date().toISOString(),
      person: this.isLinkWorkflow ? (this.application.person || null) : this.application.person,
      certificateType: this.isLinkWorkflow ? null : (rawValue.certificateType || null),
      renewal: this.selectedWorkflow === 'renewal' ? true : rawValue.renewal === true,
      previousApplication: rawValue.previousApplication || null,
      isPublic: rawValue.isPublic === true,
      firstName: rawValue.firstName || '',
      middleName: rawValue.middleName || '',
      lastName: rawValue.lastName || '',
      title: rawValue.title || '',
      ssn: rawValue.ssn || '',
      ssnExempt: true,
      ssnExemptStatement: rawValue.ssnExemptStatement || '',
      dateOfBirth: this.normalizeFormValue(rawValue.dateOfBirth),
      phone: this.isLinkWorkflow ? (rawValue.linkLookupPhone || '') : (rawValue.phone || ''),
      alternatePhone: rawValue.alternatePhone || '',
      faxPhone: rawValue.faxPhone || '',
      email: this.isLinkWorkflow ? (rawValue.linkLookupEmail || '') : (rawValue.email || ''),
      confirmEmail: this.isLinkWorkflow ? (rawValue.linkLookupEmail || '') : (rawValue.confirmEmail || ''),
      website: rawValue.website || '',
      repLabel: rawValue.repLabel || '',
      repPhone: rawValue.repPhone || '',
      repAlternatePhone: rawValue.repAlternatePhone || '',
      repFaxPhone: rawValue.repFaxPhone || '',
      repEmail: rawValue.repEmail || '',
      repConfirmEmail: rawValue.repConfirmEmail || '',
      repWebsite: rawValue.repWebsite || '',
      mailingIsPhysical: false,
      mailingStreet: rawValue.mailingStreet || '',
      mailingCity: rawValue.mailingCity || '',
      mailingState: rawValue.mailingState || '',
      mailingZip: rawValue.mailingZip || '',
      mailingCounty: rawValue.mailingState === 'OR' ? (rawValue.mailingCounty || '') : '',
      attestation: attestationValue.attestation === true,
      esignature: attestationValue.esignature || '',
    };
  }

  private persistApplication(nextStatus: string, successMessage: string | null, continuation?: () => void): void {
    this.saving = true;
    this.application.status = nextStatus;

    this.individualService.saveIndividualApplication(this.application).subscribe({
      next: (savedApplication) => {
        this.application = savedApplication;
        this.patchFormsFromApplication();
        this.applicationForm.markAsPristine();
        this.attestationForm.markAsPristine();
        this.didPersistChange = true;
        this.saving = false;
        this.loadAuxiliaryData();

        if (successMessage) {
          this.toastr.success(successMessage);
        }

        if (continuation) {
          continuation();
        }
      },
      error: () => {
        this.saving = false;
        this.toastr.error('Unable to save the Individual application.');
      }
    });
  }

  private ensureApplicationSaved(continuation: () => void): void {
    if (this.application.id > 0) {
      continuation();
      return;
    }

    this.saveDraft(false, continuation);
  }

  private saveDocumentFile(file: File, description: string | null): void {
    this.documentService
      .uploadDocument(
        file,
        { ownerType: 'PersonApplication', ownerId: this.application.id },
        DOCUMENT_CATEGORIES.personApplication,
        description ?? undefined)
      .subscribe({
        next: (savedDocument) => {
          this.documents = [...this.documents, savedDocument];
          this.filterDocumentTable();
          this.didPersistChange = true;
        },
        error: (error) => this.toastr.error(error?.error ?? 'Unable to save the application document.'),
      });
  }

  private applyLinkedIndividualToApplication(): void {
    if (!this.application.person) {
      return;
    }

    const individual = this.application.person;
    this.application.person = individual;
    this.application.firstName = individual.firstName || '';
    this.application.middleName = individual.middleName || '';
    this.application.lastName = individual.lastName || '';
    this.application.title = individual.title || '';
    this.application.ssn = individual.ssn || '';
    this.application.dateOfBirth = individual.dateOfBirth || null;
    this.patchFormsFromApplication();
  }

  private applyWorkspaceToApplication(workspace: IIndividualWorkspace): void {
    const primaryContact = workspace.contactInformations[0]?.contactInformation || null;
    const mailingAddress = workspace.addresses.find((address) => address.isCurrentMailing)?.address || workspace.addresses[0]?.address || null;

    this.application.person = workspace.individual;
    this.application.firstName = workspace.individual.firstName || '';
    this.application.middleName = workspace.individual.middleName || '';
    this.application.lastName = workspace.individual.lastName || '';
    this.application.title = workspace.individual.title || '';
    this.application.ssn = workspace.individual.ssn || '';
    this.application.dateOfBirth = workspace.individual.dateOfBirth || null;
    this.application.isPublic = workspace.individual.response === 'Yes';
    this.application.phone = primaryContact?.phone || '';
    this.application.alternatePhone = primaryContact?.cell || '';
    this.application.faxPhone = primaryContact?.fax || '';
    this.application.email = primaryContact?.email || '';
    this.application.confirmEmail = primaryContact?.email || '';
    this.application.website = primaryContact?.website || '';
    this.application.mailingStreet = mailingAddress?.street || '';
    this.application.mailingCity = mailingAddress?.city || '';
    this.application.mailingState = mailingAddress?.state || 'OR';
    this.application.mailingZip = mailingAddress?.zip || '';
    this.application.mailingCounty = mailingAddress?.county || '';
  }

  private getInitialStepIndex(): number {
    if (this.isLinkWorkflow) {
      return this.isSubmitted ? this.getLastStepIndex() : 0;
    }

    if (this.application?.id && this.isSubmitted) {
      return this.getLastStepIndex();
    }

    return 0;
  }

  private resolveInitialWorkflow(): IIndividualApplicationWorkflow {
    if (this.data.workflow === 'renewal') {
      return 'renewal';
    }

    if (this.data.workflow === 'link') {
      return 'link';
    }

    if (this.data.workflow === 'new') {
      return 'new';
    }

    return 'chooser';
  }

  private beginWorkflow(workflow: 'new' | 'link'): void {
    this.selectedWorkflow = workflow;
    this.resetWorkflowState();
  }

  private resetWorkflowState(): void {
    this.application = this.createEmptyApplication();
    this.documentsFilterString = '';
    this.documents = [];
    this.filteredDocuments = [];
    this.linkLookupLoading = false;
    this.renewalOptions = [];
    this.currentStep = 0;
    this.validationSummaryRequested = false;
    this.resetLinkSelectionState();
    this.patchFormsFromApplication();
    this.applicationForm.enable({ emitEvent: false });
    this.attestationForm.enable({ emitEvent: false });
    this.attestationForm.reset({ attestation: false, esignature: '' }, { emitEvent: false });
    this.applicationForm.markAsPristine();
    this.applicationForm.markAsUntouched();
    this.attestationForm.markAsPristine();
    this.attestationForm.markAsUntouched();
    this.updateSsnRequirement();
  }

  private navigateToStep(stepIndex: number): void {
    const boundedStep = Math.max(0, Math.min(stepIndex, this.getLastStepIndex()));
    const previousStep = this.currentStep;

    if (boundedStep === previousStep) {
      return;
    }

    if (this.isLinkWorkflow && previousStep === 0 && boundedStep > 0) {
      this.currentStep = boundedStep;

      if (this.canAutoSearchLinkLookup()) {
        this.loadLinkIndividualDetails();
      }

      return;
    }

    this.currentStep = boundedStep;
  }

  private getLastStepIndex(): number {
    return this.getStepLabels().length - 1;
  }

  private getStepLabels(): string[] {
    if (!this.isLinkWorkflow) {
      return this.standardStepLabels;
    }

    return this.isSubmitted ? this.submittedLinkStepLabels : this.linkStepLabels;
  }

  private isSubmittedLinkConfirmationStep(): boolean {
    return this.isLinkWorkflow && this.isSubmitted && this.currentStep === this.getLastStepIndex();
  }

  private hasText(value: any): boolean {
    return (value ?? '').toString().trim().length > 0;
  }

  private resolveValidationFieldSelectors(error: IWizardValidationError): string[] {
    if (this.applicationForm.get(error.field) || this.attestationForm.get(error.field)) {
      return this.buildControlSelectors(error.field);
    }

    if (error.field === 'lookupRequirement') {
      return [
        ...this.buildControlSelectors('dateOfBirth'),
        ...this.buildControlSelectors('linkLookupPhone'),
        ...this.buildControlSelectors('linkLookupEmail'),
      ];
    }

    return [];
  }

  private buildControlSelectors(controlName: string): string[] {
    return [
      `[formcontrolname="${controlName}"]`,
      `[formControlName="${controlName}"]`,
    ];
  }

  private getDefaultInvalidFieldSelectors(): string[] {
    return [
      '.wizard-shell .scroll-container .field-error input',
      '.wizard-shell .scroll-container .field-error textarea',
      '.wizard-shell .scroll-container .field-error .mat-select-trigger',
      '.wizard-shell .scroll-container .mat-form-field-invalid input',
      '.wizard-shell .scroll-container .mat-form-field-invalid textarea',
      '.wizard-shell .scroll-container .mat-form-field-invalid .mat-select-trigger',
      '.wizard-shell .scroll-container .mat-checkbox.ng-invalid input',
    ];
  }

  private focusFirstVisibleBySelector(selectors: string[]): boolean {
    for (const selector of selectors) {
      const element = this.findFirstVisibleElement(selector);
      if (!element) {
        continue;
      }

      this.focusElement(element);
      return true;
    }

    return false;
  }

  private findFirstVisibleElement(selector: string): HTMLElement | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const elements = Array.from(document.querySelectorAll(selector)) as HTMLElement[];
    return elements.find((element) => this.isVisibleElement(element)) || null;
  }

  private isVisibleElement(element: HTMLElement): boolean {
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  }

  private focusElement(element: HTMLElement): void {
    const focusTarget = element.matches('input, textarea, select, button, [tabindex]')
      ? element
      : element.querySelector<HTMLElement>('input, textarea, select, button, .mat-select-trigger, .mat-radio-input, [tabindex]');

    if (!focusTarget) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    focusTarget.focus({ preventScroll: true });
    focusTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  private getVisibleScrollContainer(): HTMLElement | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const containers = Array.from(document.querySelectorAll('.wizard-shell .scroll-container')) as HTMLElement[];
    return containers.find((container) => this.isVisibleElement(container)) || null;
  }

  private hasValidationInteraction(control: AbstractControl | null): boolean {
    return !!control && (this.validationSummaryRequested || control.touched || control.dirty);
  }

  private addControlError(errors: IWizardValidationError[], control: AbstractControl | null, field: string, label: string, step: string): void {
    if (!control || !control.invalid || !this.hasValidationInteraction(control)) {
      return;
    }

    errors.push({ field, label: this.getValidationLabel(control, label), step });
  }

  private addCustomError(errors: IWizardValidationError[], condition: boolean, field: string, label: string, step: string): void {
    if (!condition) {
      return;
    }

    errors.push({ field, label, step });
  }

  private getValidationLabel(control: AbstractControl, defaultLabel: string): string {
    if (control.hasError('emailMismatch')) {
      return defaultLabel + ' must match';
    }

    if (control.hasError('email')) {
      return defaultLabel + ' must be a valid email address';
    }

    if (control.hasError('pattern')) {
      if (defaultLabel.toLowerCase().includes('zip')) {
        return defaultLabel + ' must be a valid ZIP code';
      }

      if (defaultLabel.toLowerCase().includes('phone')) {
        return defaultLabel + ' must be a valid phone number';
      }

      if (defaultLabel.toLowerCase().includes('social security')) {
        return defaultLabel + ' must be a valid SSN';
      }
    }

    return defaultLabel;
  }

  private getValidationErrors(): IWizardValidationError[] {
    const errors: IWizardValidationError[] = [];

    if (this.showWorkflowChooser) {
      return errors;
    }

    if (this.isLinkWorkflow) {
      this.addControlError(errors, this.applicationForm.get('firstName'), 'firstName', 'First Name', 'Individual Information');
      this.addControlError(errors, this.applicationForm.get('lastName'), 'lastName', 'Last Name', 'Individual Information');
      this.addCustomError(errors, !this.hasLinkLookupSupplementalValue(), 'lookupRequirement', 'Date of Birth, Phone Number, or Email', 'Individual Information');
      this.addControlError(errors, this.applicationForm.get('linkLookupPhone'), 'linkLookupPhone', 'Phone Number', 'Individual Information');
      this.addControlError(errors, this.applicationForm.get('linkLookupEmail'), 'linkLookupEmail', 'Email', 'Individual Information');
      this.addControlError(errors, this.attestationForm.get('attestation'), 'attestation', 'Attestation', 'Signature and Acknowledgments');
      this.addControlError(errors, this.attestationForm.get('esignature'), 'esignature', 'Electronic Signature', 'Signature and Acknowledgments');
      return this.dedupeValidationErrors(errors);
    }

    this.addControlError(errors, this.applicationForm.get('certificateType'), 'certificateType', 'Certificate Type', 'Application Information');
    this.addCustomError(
      errors,
      this.isRenewalApplication
        && !this.hasText(this.applicationForm.get('previousApplication')?.value)
        && this.hasValidationInteraction(this.applicationForm.get('previousApplication')),
      'previousApplication',
      'Certificate to Renew',
      'Application Information'
    );

    this.addControlError(errors, this.applicationForm.get('firstName'), 'firstName', 'First Name', 'Individual Information');
    this.addControlError(errors, this.applicationForm.get('lastName'), 'lastName', 'Last Name', 'Individual Information');
    this.addControlError(errors, this.applicationForm.get('ssn'), 'ssn', 'Social Security Number', 'Individual Information');

    const contactControls: Array<[string, string]> = [
      ['phone', 'Primary Phone'],
      ['email', 'Email'],
      ['confirmEmail', 'Confirm Email'],
    ];

    contactControls.forEach(([controlName, label]) => {
      this.addControlError(errors, this.applicationForm.get(controlName), controlName, label, 'Contact Information');
    });

    this.addCustomError(
      errors,
      !this.hasText(this.applicationForm.get('mailingStreet')?.value) && this.hasValidationInteraction(this.applicationForm.get('mailingStreet')),
      'mailingStreet',
      'Mailing Street Address',
      'Mailing Address'
    );
    this.addCustomError(
      errors,
      !this.hasText(this.applicationForm.get('mailingCity')?.value) && this.hasValidationInteraction(this.applicationForm.get('mailingCity')),
      'mailingCity',
      'Mailing City',
      'Mailing Address'
    );
    this.addCustomError(
      errors,
      !this.hasText(this.applicationForm.get('mailingState')?.value) && this.hasValidationInteraction(this.applicationForm.get('mailingState')),
      'mailingState',
      'Mailing State',
      'Mailing Address'
    );
    this.addCustomError(
      errors,
      !this.hasText(this.applicationForm.get('mailingZip')?.value) && this.hasValidationInteraction(this.applicationForm.get('mailingZip')),
      'mailingZip',
      'Mailing ZIP Code',
      'Mailing Address'
    );

    this.addControlError(errors, this.attestationForm.get('attestation'), 'attestation', 'Attestation', 'Review & Submit');
    this.addControlError(errors, this.attestationForm.get('esignature'), 'esignature', 'Electronic Signature', 'Review & Submit');

    return this.dedupeValidationErrors(errors);
  }

  private getStepValidationErrors(stepIndex: number): IWizardValidationError[] {
    const targetStep = this.getStepLabels()[stepIndex];
    return this.getValidationErrors().filter((error) => error.step === targetStep);
  }

  private dedupeValidationErrors(errors: IWizardValidationError[]): IWizardValidationError[] {
    const seen = new Set<string>();
    return errors.filter((error) => {
      const key = error.step + '|' + error.field + '|' + error.label;
      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
  }

  private handleValidationFailure(errors: IWizardValidationError[], message: string): void {
    this.validationSummaryRequested = true;
    this.isFeedbackCollapsed = false;

    if (errors.length > 0) {
      this.toastr.error(message + ' ' + errors.length + ' item(s) need attention.', 'Validation Error', {
        timeOut: 5000,
      });
      this.scrollValidationSummaryIntoView();
      return;
    }

    this.toastr.error(message, 'Validation Error', {
      timeOut: 5000,
    });
  }

  private getValidationFeedbackText(): string {
    const validationErrors = this.getValidationErrors();
    if (!this.validationSummaryRequested || validationErrors.length === 0) {
      return '';
    }

    const groupedErrors = new Map<string, string[]>();
    validationErrors.forEach((error) => {
      const existing = groupedErrors.get(error.step) || [];
      if (!existing.includes(error.label)) {
        existing.push(error.label);
      }
      groupedErrors.set(error.step, existing);
    });

    const lines = ['--- Missing Required Fields ---'];
    this.getStepLabels().forEach((stepLabel) => {
      const stepErrors = groupedErrors.get(stepLabel);
      if (!stepErrors || stepErrors.length === 0) {
        return;
      }

      lines.push(stepLabel + ':');
      stepErrors.forEach((label) => lines.push('- ' + label));
      lines.push('');
    });

    return lines.join('\n').trim();
  }

  private scrollValidationSummaryIntoView(): void {
    setTimeout(() => {
      this.validationSummaryCard?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  updateSsnRequirement(): void {
    const ssnControl = this.applicationForm.get('ssn');
    if (!ssnControl) {
      return;
    }

    this.applicationForm.get('ssnExempt')?.setValue(true, { emitEvent: false });
    if (this.canEdit && ssnControl.disabled) {
      ssnControl.enable({ emitEvent: false });
    }

    ssnControl.setValidators([Validators.pattern(/^(?:\*{7}|^\(?([0-9]{3})\)?-?([0-9]{2})-?([0-9]{4})).*$/)]);
    ssnControl.updateValueAndValidity({ emitEvent: false });
  }

  private isDraftStatus(status: string | null): boolean {
    return status === APPLICATION_STATUS.inProgress || status === APPLICATION_STATUS.actionNeeded || !status;
  }

  private isApplicationExpired(application: IIndividualApplication | null | undefined): boolean {
    if (!application?.applicationDate) {
      return false;
    }

    const status = this.normalizeStatus(application.status);
    if (status === this.normalizeStatus(APPLICATION_STATUS.approved) || status === this.normalizeStatus(APPLICATION_STATUS.cancelled)) {
      return false;
    }

    const currentDate = this.toDateOnly(new Date());
    const applicationDate = this.toDateOnly(new Date(application.applicationDate));
    const diffInDays = (currentDate.getTime() - applicationDate.getTime()) / (1000 * 3600 * 24);
    return diffInDays > 90;
  }

  private normalizeStatus(status: string | null | undefined): string {
    return (status || '').toString().trim().toLowerCase();
  }

  private toDateOnly(value: string | Date): Date {
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private formatUsDate(value: string | Date | null | undefined, fallback = 'Unknown'): string {
    if (!value) {
      return fallback;
    }

    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return fallback;
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }

  private toDateValue(value: string | null | undefined): Date | string {
    return value ? new Date(value) : '';
  }

  private normalizeFormValue(value: any): any {
    if (value === '') {
      return null;
    }

    if (!value) {
      return value;
    }

    if (value instanceof Date) {
      return value.toISOString();
    }

    return value;
  }

  private buildRenewalOptions(certificates: IIndividualCertificate[]): IRenewalOption[] {
    return (certificates || [])
      .filter((certificate) => !!certificate.controlNumber)
      .sort((left, right) => {
        const leftDate = left.expirationDate ? new Date(left.expirationDate).getTime() : 0;
        const rightDate = right.expirationDate ? new Date(right.expirationDate).getTime() : 0;
        return rightDate - leftDate;
      })
      .map((certificate) => ({
        certificate,
        label: certificate.codeType + ' - ' + (certificate.controlNumber || 'No Control #') + ' - Expires ' + this.formatUsDate(certificate.expirationDate),
      }));
  }

  private selectedLinkIndividualMatches(): boolean {
    if (!this.application?.person) {
      return false;
    }

    const lastName = (this.applicationForm.get('lastName')?.value || '').trim().toLowerCase();

    if (!this.linkFirstNameMatches(this.application.person)) {
      return false;
    }

    if (lastName !== (this.application.person.lastName || '').trim().toLowerCase()) {
      return false;
    }

    return true;
  }

  private matchesLinkSearch(individual: IIndividual): boolean {
    const lastName = (this.applicationForm.get('lastName')?.value || '').trim().toLowerCase();

    if (!this.linkFirstNameMatches(individual)) {
      return false;
    }

    if ((individual.lastName || '').trim().toLowerCase() !== lastName) {
      return false;
    }

    return true;
  }

  private linkFirstNameMatches(individual: IIndividual): boolean {
    const firstName = (this.applicationForm.get('firstName')?.value || '').trim().toLowerCase();
    const individualFirstName = (individual?.firstName || '').trim().toLowerCase();

    if (individualFirstName === firstName) {
      return true;
    }

    const middleName = (individual?.middleName || '').trim().toLowerCase();
    return middleName.length > 0 && (individualFirstName + ' ' + middleName) === firstName;
  }

  private canAutoSearchLinkLookup(): boolean {
    const firstName = (this.applicationForm.get('firstName')?.value || '').trim();
    const lastName = (this.applicationForm.get('lastName')?.value || '').trim();
    const phoneControl = this.applicationForm.get('linkLookupPhone');
    const emailControl = this.applicationForm.get('linkLookupEmail');
    return firstName.length > 0
      && lastName.length > 0
      && this.hasLinkLookupSupplementalValue()
      && !phoneControl?.hasError('pattern')
      && !emailControl?.hasError('email');
  }

  private linkLookupRequestMatchesCurrentForm(firstName: string, lastName: string, dateOfBirth: string, phone: string, email: string): boolean {
    return (this.applicationForm.get('firstName')?.value || '').trim().toLowerCase() === firstName
      && (this.applicationForm.get('lastName')?.value || '').trim().toLowerCase() === lastName
      && this.normalizeDateString(this.applicationForm.get('dateOfBirth')?.value) === dateOfBirth
      && (this.applicationForm.get('linkLookupPhone')?.value || '').trim() === phone
      && (this.applicationForm.get('linkLookupEmail')?.value || '').trim() === email;
  }

  private finalizeLinkLookup(): void {
    this.linkLookupLoading = false;
  }

  public hasLinkLookupSupplementalValue(): boolean {
    return this.hasText(this.applicationForm.get('dateOfBirth')?.value)
      || this.hasText(this.applicationForm.get('linkLookupPhone')?.value)
      || this.hasText(this.applicationForm.get('linkLookupEmail')?.value);
  }

  private normalizeDateString(value: string | Date | null | undefined): string {
    if (!value) {
      return '';
    }

    return this.toDateOnly(value).toISOString();
  }

  private resetLinkSelectionState(): void {
    if (this.application) {
      this.application.person = null;
    }

    this.linkApprovalContactEmails = [];
    this.matchedLinkApprovalEmail = null;
    this.linkLookupMatchStatus = 'unknown';
  }

  private loadLinkApprovalContactEmails(personId: number): void {
    if (!personId || personId <= 0) {
      this.linkApprovalContactEmails = [];
      this.matchedLinkApprovalEmail = null;
      return;
    }

    this.individualService.getIndividualContactInformations(personId).subscribe({
      next: (contactInformations) => {
        const emails = (contactInformations || [])
          .map((contact) => contact?.contactInformation?.email || '')
          .map((email) => (email || '').trim())
          .filter((email, index, list) => email.length > 0
            && list.findIndex((candidate) => candidate.toLowerCase() === email.toLowerCase()) === index);

        this.linkApprovalContactEmails = emails;
        this.matchedLinkApprovalEmail = this.resolveMatchedLinkApprovalEmail(emails);
      },
      error: () => {
        this.linkApprovalContactEmails = [];
        this.matchedLinkApprovalEmail = null;
      }
    });
  }

  private resolveMatchedLinkApprovalEmail(emails: string[]): string | null {
    const enteredEmail = (this.applicationForm.get('linkLookupEmail')?.value || '').trim();
    if (!enteredEmail) {
      return null;
    }

    const normalizedEnteredEmail = enteredEmail.toLowerCase();
    return emails.find((email) => email.toLowerCase() === normalizedEnteredEmail) || enteredEmail;
  }

  private hasActiveIndividualAffiliation(personId: number, affiliations: any[]): boolean {
    if (!personId || personId <= 0) {
      return false;
    }

    return (affiliations || []).some((affiliation) => {
      const status = (affiliation?.status || '').toString().trim().toLowerCase();
      const affiliationPersonId = Number(affiliation?.person?.id || 0);
      return status === 'active' && affiliationPersonId === personId;
    });
  }

  private guardDuplicateIndividualLinkSubmission(personId: number, continuation: () => void): void {
    this.commonService.getAccountAffiliation().subscribe({
      next: (affiliations) => {
        if (this.hasActiveIndividualAffiliation(personId, affiliations)) {
          this.toastr.warning('Your account is already linked to this Individual.', 'Already Linked');
          return;
        }

        continuation();
      },
      error: () => continuation(),
    });
  }

  private getLinkApprovalRecipientSummary(): string {
    if (this.hasMultipleLinkMatches) {
      return `Multiple matching Individuals were identified. This request will be sent to <strong>${this.approvalEmail}</strong> for staff review.`;
    }

    if (this.hasUniqueLinkMatch) {
      if (this.showMatchedLinkApprovalEmail) {
        return `A unique matching Individual was identified. The individual will be emailed at <strong>${this.matchedLinkApprovalEmail}</strong>.`;
      }

      return 'A unique matching Individual was identified. The individual will be emailed.';
    }

    if (this.hasNoLinkMatch) {
      return `No matching Individual was identified. This request will be sent to <strong>${this.approvalEmail}</strong> for staff review.`;
    }

    return `Matching information could not be verified. This request will be sent to <strong>${this.approvalEmail}</strong> for staff review.`;
  }

  private getWorkflowFromApplication(application: IIndividualApplication): IIndividualApplicationWorkflow {
    if (application.renewal) {
      return 'renewal';
    }

    if (!application.certificateType && this.normalizeStatus(application.status) !== this.normalizeStatus(APPLICATION_STATUS.inProgress)) {
      return 'link';
    }

    return 'new';
  }

  private loadAuxiliaryData(): void {
    if (this.application.id <= 0) {
      this.documents = [];
      this.filteredDocuments = [];
      return;
    }

    this.documentService.getDocuments({ ownerType: 'PersonApplication', ownerId: this.application.id }).subscribe({
      next: (documents) => {
        this.documents = documents.sort((left, right) => left.fileName.localeCompare(right.fileName));
        this.filterDocumentTable();
      },
      error: () => {
        this.documents = [];
        this.filteredDocuments = [];
      }
    });

    this.individualService.getIndividualApplicationDeficiencies(this.application.id).subscribe({
      next: (deficiencies) => {
        this.application.deficiencies = deficiencies;
      },
      error: () => {
        this.application.deficiencies = [];
      }
    });
  }
}